import { ComboButton, MenuItem, MenuItemDivider } from "@carbon/react";
import { useModal } from "@flaminem/ui-components";
import React from "react";

import { AuthorizedItem } from "src/authentication/AuthorizedMenu.tsx";
import { Scope, ScopeResource } from "src/authentication/PermissionsProvider.tsx";
import MenuButton from "src/components/MenuButton/MenuButton.tsx";
import { ModalTransition } from "src/components/Modal";
import { useMediaQuery, BREAKPOINT_LARGE } from "src/hooks/useMediaQuery.ts";
import { useT } from "src/internationalization";
import { AssignAlertModal } from "src/pages/AlertDetails/assign-alert/AssignAlertModal.tsx";
import { ValidateAlertModal } from "src/pages/AlertDetails/validate-alert/ValidateAlertModal/ValidateAlertModal";
import { Alert } from "src/types/alert.ts";

type ValidateAlertModalButtonsProps = {
  alert: Alert;
  caseId?: string;
};

const ValidateAlertModalButtons: React.FC<ValidateAlertModalButtonsProps> = ({ alert, caseId }) => {
  const t = useT();
  const matches = useMediaQuery(BREAKPOINT_LARGE);

  const [confirmModal, assignModal] = [useModal(), useModal()];

  const canShowActions = alert?.isAssigned && !alert?.isAlertValidated && alert.id;

  const renderAssignMenuItem = () => (
    <AuthorizedItem
      requiredPermissions={[{ resource: ScopeResource.ALERT, scopes: [Scope.ASSIGN] }]}
    >
      <MenuItem label={t("general.actions.assign")} onClick={assignModal.openModal} />
    </AuthorizedItem>
  );

  const renderActionButtons = () => (
    <>
      <AuthorizedItem
        requiredPermissions={[{ resource: ScopeResource.ALERT, scopes: [Scope.VALIDATE] }]}
      >
        {!matches ? (
          <MenuItem label={t("page.alert.button.confirm")} onClick={confirmModal.openModal} />
        ) : (
          <ComboButton
            label={t("page.alert.button.confirm")}
            onClick={confirmModal.openModal}
            tooltipAlignment="top-right"
          >
            {!alert?.isAlertValidated && renderAssignMenuItem()}
          </ComboButton>
        )}
      </AuthorizedItem>
    </>
  );

  const renderActions = () => {
    if (!canShowActions) return null;

    if (!matches) {
      return (
        <MenuButton label={t("general.actions.all")} menuAlignment="bottom-end">
          {renderActionButtons()}
          <MenuItemDivider />
          {!alert.isAlertValidated && renderAssignMenuItem()}
        </MenuButton>
      );
    }

    return renderActionButtons();
  };

  return (
    <>
      <div className="flex">{renderActions()}</div>
      <ModalTransition>
        {assignModal.isModalOpen && (
          <AssignAlertModal
            alert={alert}
            onSubmit={assignModal.closeModal}
            onClose={assignModal.closeModal}
          />
        )}
        {confirmModal.isModalOpen && (
          <ValidateAlertModal
            onClose={confirmModal.closeModal}
            alert={alert}
            caseId={caseId ?? ""}
          />
        )}
      </ModalTransition>
    </>
  );
};

export default ValidateAlertModalButtons;
